import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { legacySiteId, type LegacySiteId } from './LegacySite.js'
import { userId, type UserId } from './User.js'

/** Represents the table public.legacy_site_reading_cutover_ */
export default interface LegacySiteReadingCutoverTable {
  /** Database type: pg_catalog.int4 */
  siteId: ColumnType<LegacySiteId, LegacySiteId, LegacySiteId>

  /** Database type: pg_catalog.timestamptz */
  liftedAt: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.int4 */
  mongoDocs: ColumnType<number, number, number>

  /** Database type: pg_catalog.int4 */
  storeRows: ColumnType<number, number, number>

  /** Database type: pg_catalog.timestamptz */
  reconciledAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.int4 */
  reconcileMismatches: ColumnType<number | null, number | null, number | null>

  /** Database type: pg_catalog.timestamptz */
  linesIdenticalAt: ColumnType<
    Date | null,
    Date | string | null,
    Date | string | null
  >

  /** Database type: pg_catalog.timestamptz */
  cutOverAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  cutOverBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.text */
  notes: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date, Date | string | undefined, Date | string>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date, Date | string | undefined, Date | string>
}

export type LegacySiteReadingCutover = Selectable<LegacySiteReadingCutoverTable>

export type LegacySiteReadingCutoverInitializer =
  Insertable<LegacySiteReadingCutoverTable>

export type LegacySiteReadingCutoverMutator =
  Updateable<LegacySiteReadingCutoverTable>

export const legacySiteReadingCutover = z.object({
  siteId: legacySiteId,
  liftedAt: z.coerce.date(),
  mongoDocs: z.number(),
  storeRows: z.number(),
  reconciledAt: z.coerce.date().nullable().optional(),
  reconcileMismatches: z.number().nullable().optional(),
  linesIdenticalAt: z.coerce.date().nullable().optional(),
  cutOverAt: z.coerce.date().nullable().optional(),
  cutOverBy: userId.nullable().optional(),
  notes: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
}) as unknown as z.Schema<LegacySiteReadingCutover>

export const legacySiteReadingCutoverInitializer = z.object({
  siteId: legacySiteId,
  liftedAt: z.coerce.date(),
  mongoDocs: z.number(),
  storeRows: z.number(),
  reconciledAt: z.coerce.date().optional().nullable(),
  reconcileMismatches: z.number().optional().nullable(),
  linesIdenticalAt: z.coerce.date().optional().nullable(),
  cutOverAt: z.coerce.date().optional().nullable(),
  cutOverBy: userId.optional().nullable(),
  notes: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}) as unknown as z.Schema<LegacySiteReadingCutoverInitializer>

export const legacySiteReadingCutoverMutator = z.object({
  siteId: legacySiteId.optional(),
  liftedAt: z.coerce.date().optional(),
  mongoDocs: z.number().optional(),
  storeRows: z.number().optional(),
  reconciledAt: z.coerce.date().optional().nullable(),
  reconcileMismatches: z.number().optional().nullable(),
  linesIdenticalAt: z.coerce.date().optional().nullable(),
  cutOverAt: z.coerce.date().optional().nullable(),
  cutOverBy: userId.optional().nullable(),
  notes: z.string().optional().nullable(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
}) as unknown as z.Schema<LegacySiteReadingCutoverMutator>
